import { makeAutoObservable, observable, runInAction } from 'mobx';
import { getDepthCoords, CurvePoint } from '../../api/curvesCache';
import { LithologyImage } from '../../lithologyImages';
import { immutableSplice } from '../../utils';

export class CuttingsCoordinates {
  curveId: number;

  points: CurvePoint[] = [];

  inProgress = false;

  inDownloading = false;

  errors = null;

  onData?: number;

  toData?: number;

  lithologyImage: LithologyImage;

  minValue = 0;

  maxValue = 100;

  fetched = false;

  constructor(curveId: number, lithologyImage: LithologyImage) {
    makeAutoObservable(this, {
      points: observable.ref,
    });
    this.curveId = curveId;
    this.lithologyImage = lithologyImage;
  }

  async refreshCoords({ force }: { force?: boolean }) {
    if (!force && (this.points.length > 0 || this.inProgress || this.inDownloading)) {
      return;
    }
    this.inProgress = true;
    this.errors = null;
    try {
      const data = await getDepthCoords(this.curveId);
      runInAction(() => {
        if (Array.isArray(data)) {
          this.points = data;
          this.onData = data.at(0)?.key;
          this.toData = data.at(-1)?.key;
          this.fetched = true;
        } else {
          this.inDownloading = true;
        }
      });
    } catch (e: any) {
      runInAction(() => {
        this.errors = e.response && e.response.body && e.response.body.errors;
      });
      throw e;
    } finally {
      runInAction(() => {
        this.inProgress = false;
      });
    }
  }

  getPoint(y: number) {
    return this.points.find((point, index, array) => {
      const nextPoint = array.at(index + 1);
      if (!nextPoint) return true;
      if (point.key <= y && y < nextPoint.key) return true;
      return false;
    });
  }

  addPoint(point: CurvePoint) {
    const index = this.points.findIndex((p, i) => {
      const next = this.points.at(i + 1);
      if (next) {
        return p.key < point.key && point.key < next.key;
      }
      return false;
    });
    if (index > -1 && index < this.points.length) {
      this.points = immutableSplice(this.points, index + 1, 0, point);
    }
  }

  addPoints(points: CurvePoint[]) {
    this.points = this.points.concat(...points.filter((p) => p.key > (this.toData || 0)));
  }
}
